/* Mckwurst Designs theme behavior: sticky header state, announcement rotation,
   mobile menu dialog, cart count refresh, form status focus. No dependencies. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* The inline header menu replaces the hamburger menu from this width up. Keep it equal to the
     nav breakpoint in layout.css. */
  var NAV_QUERY = '(min-width: 1340px)';

  /* Sticky glass: toggle data-scrolled when the top sentinel leaves the viewport. */
  function initStickyHeader() {
    var top = document.querySelector('[data-site-top]');
    var sentinel = document.querySelector('[data-scroll-sentinel]');
    if (!top || !sentinel || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (entries) {
      top.setAttribute('data-scrolled', entries[0].isIntersecting ? 'false' : 'true');
    }).observe(sentinel);
  }

  /* Publish the sticky group's height so scroll-padding-top clears it (WCAG 2.4.11). */
  function initSiteTopHeight() {
    var top = document.querySelector('[data-site-top]');
    if (!top) return;
    var root = document.documentElement;
    function publish() {
      root.style.setProperty('--site-top-height', top.offsetHeight + 'px');
    }
    publish();
    if ('ResizeObserver' in window) new ResizeObserver(publish).observe(top);
    else window.addEventListener('resize', publish);
  }

  /* Announcement bar: crossfade one message at a time. Pauses on hover and focus,
     and the toggle button pauses it until pressed again. */
  function initAnnouncements() {
    document.querySelectorAll('[data-announcement]').forEach(function (bar) {
      var messages = bar.querySelectorAll('[data-announcement-message]');
      if (messages.length < 2) return;
      var interval = Number(bar.getAttribute('data-interval')) || 5000;
      var index = 0;
      var paused = false;
      var userPaused = false;
      var timer = null;
      var toggle = bar.querySelector('[data-announcement-toggle]');

      function show(next) {
        messages[index].classList.remove('is-active');
        index = next;
        messages[index].classList.add('is-active');
      }

      function start() {
        stop();
        if (reduceMotion.matches || userPaused) return;
        timer = window.setInterval(function () {
          if (!paused) show((index + 1) % messages.length);
        }, interval);
      }

      function stop() {
        if (timer) window.clearInterval(timer);
        timer = null;
      }

      if (toggle) {
        toggle.addEventListener('click', function () {
          userPaused = !userPaused;
          toggle.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
          if (userPaused) stop();
          else start();
        });
      }

      /* Inactive messages stay in the accessibility tree. Focusing one reveals it. */
      messages.forEach(function (message, i) {
        message.addEventListener('focusin', function () {
          if (i !== index) show(i);
        });
      });

      bar.addEventListener('mouseenter', function () { paused = true; });
      bar.addEventListener('mouseleave', function () {
        var pressed = toggle && toggle.getAttribute('aria-pressed') === 'true';
        if (!pressed && !bar.contains(document.activeElement)) paused = false;
      });
      bar.addEventListener('focusin', function () { paused = true; });
      bar.addEventListener('focusout', function () { paused = false; });
      reduceMotion.addEventListener('change', start);
      start();
    });
  }

  /* Mobile menu: native dialog gives focus trap, Esc, and focus return. */
  function initMobileMenu() {
    var dialog = document.getElementById('MobileMenu');
    var openers = document.querySelectorAll('[data-menu-open]');
    if (!dialog || typeof dialog.showModal !== 'function') return;

    function setExpanded(value) {
      openers.forEach(function (button) { button.setAttribute('aria-expanded', value); });
    }

    openers.forEach(function (button) {
      button.addEventListener('click', function () {
        dialog.showModal();
        setExpanded('true');
      });
    });
    dialog.querySelectorAll('[data-menu-close]').forEach(function (button) {
      button.addEventListener('click', function () { dialog.close(); });
    });
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', function () { setExpanded('false'); });
    window.matchMedia(NAV_QUERY).addEventListener('change', function (event) {
      if (event.matches && dialog.open) dialog.close();
    });
  }

  /* Mobile menu groups: a button with aria-expanded shows or hides the sub list. */
  function initMobileGroups() {
    document.querySelectorAll('[data-mobile-toggle]').forEach(function (button) {
      var list = document.getElementById(button.getAttribute('aria-controls'));
      if (!list) return;
      button.addEventListener('click', function () {
        var open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        list.hidden = !open;
      });
    });
  }

  /* Desktop dropdowns. A [data-nav-item] holds a button and a list of links. The disclosure opens on
     click, Enter, Space, and ArrowDown, and on hover for a mouse. Escape closes it and returns focus to
     the button. Arrow keys, Home, and End move through the links. Focus leaving the item, or a click
     outside it, closes it. */
  function initNavDropdowns() {
    var items = Array.prototype.slice.call(document.querySelectorAll('[data-nav-item]'));
    if (!items.length) return;
    var mouseQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    function setOpen(item, open) {
      var trigger = item.querySelector('[data-nav-trigger]');
      item.classList.toggle('is-open', open);
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function closeAll(except) {
      items.forEach(function (other) { if (other !== except) setOpen(other, false); });
    }

    items.forEach(function (item) {
      var trigger = item.querySelector('[data-nav-trigger]');
      var panel = item.querySelector('[data-nav-panel]');
      var links = Array.prototype.slice.call(panel.querySelectorAll('a'));
      var timer = null;
      var openedByHover = false;

      function open() {
        window.clearTimeout(timer);
        closeAll(item);
        setOpen(item, true);
      }

      trigger.addEventListener('click', function () {
        if (openedByHover) { openedByHover = false; return; }
        if (item.classList.contains('is-open')) setOpen(item, false);
        else open();
      });

      trigger.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          event.stopPropagation();
          open();
          if (links[0]) links[0].focus();
        }
      });

      item.addEventListener('keydown', function (event) {
        if (!item.classList.contains('is-open')) return;
        var index = links.indexOf(document.activeElement);
        if (event.key === 'Escape') {
          event.preventDefault();
          setOpen(item, false);
          trigger.focus();
        } else if (index !== -1 && event.key === 'ArrowDown') {
          event.preventDefault();
          links[(index + 1) % links.length].focus();
        } else if (index !== -1 && event.key === 'ArrowUp') {
          event.preventDefault();
          if (index === 0) trigger.focus();
          else links[index - 1].focus();
        } else if (index !== -1 && event.key === 'Home') {
          event.preventDefault();
          links[0].focus();
        } else if (index !== -1 && event.key === 'End') {
          event.preventDefault();
          links[links.length - 1].focus();
        }
      });

      item.addEventListener('focusout', function (event) {
        if (!event.relatedTarget || !item.contains(event.relatedTarget)) setOpen(item, false);
      });

      item.addEventListener('pointerenter', function (event) {
        if (event.pointerType !== 'mouse' || !mouseQuery.matches) return;
        if (!item.classList.contains('is-open')) openedByHover = true;
        open();
      });

      item.addEventListener('pointerleave', function (event) {
        if (event.pointerType !== 'mouse') return;
        openedByHover = false;
        window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          if (!item.contains(document.activeElement)) setOpen(item, false);
        }, 160);
      });
    });

    document.addEventListener('click', function (event) {
      items.forEach(function (item) { if (!item.contains(event.target)) setOpen(item, false); });
    });
    window.matchMedia(NAV_QUERY).addEventListener('change', function () { closeAll(null); });
  }

  /* Cart count: other scripts dispatch "cart:updated" on document after an add or change. */
  function initCartCount() {
    function render(count) {
      document.querySelectorAll('[data-cart-count]').forEach(function (badge) {
        badge.textContent = count;
        badge.hidden = count === 0;
      });
      document.querySelectorAll('[data-cart-link]').forEach(function (link) {
        var key = count === 0 ? 'zero' : count === 1 ? 'one' : 'other';
        var template = link.getAttribute('data-label-' + key);
        if (template) link.setAttribute('aria-label', template.replace('__COUNT__', count));
      });
    }

    function announce(count) {
      document.querySelectorAll('[data-cart-status]').forEach(function (region) {
        var template = region.getAttribute(count === 1 ? 'data-label-one' : 'data-label');
        if (!template) return;
        var text = template.replace('__COUNT__', count);
        if (region.textContent === text) {
          region.textContent = '';
          window.requestAnimationFrame(function () { region.textContent = text; });
        } else {
          region.textContent = text;
        }
      });
    }

    document.addEventListener('cart:updated', function () {
      fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) { return response.json(); })
        .then(function (cart) { render(cart.item_count); announce(cart.item_count); })
        .catch(function () {});
    });
  }

  /* Newsletter result: the server renders the status only after a post, so move focus to it. */
  function initFormStatus() {
    var status = document.querySelector('[data-form-status]');
    if (status) status.focus();
  }

  /* Deferred images. An <img data-defer> has no src or srcset until it is within 200px of the viewport,
     so below-fold pictures do not compete with the page's main image. An element marked
     data-defer-group (a scroll strip, an option group) loads all the images it holds at once, because
     a picture clipped by a scroller never counts as near the viewport. */
  function loadDeferred(img) {
    if (!img.hasAttribute('data-defer')) return;
    img.removeAttribute('data-defer');
    img.loading = 'eager';
    var srcset = img.getAttribute('data-srcset');
    if (srcset) { img.srcset = srcset; img.removeAttribute('data-srcset'); }
    var src = img.getAttribute('data-src');
    if (src) { img.src = src; img.removeAttribute('data-src'); }
  }

  function initDeferredImages(scope) {
    var root = scope || document;
    var images = Array.prototype.slice.call(root.querySelectorAll('img[data-defer]'));
    if (!images.length) return;
    if (!('IntersectionObserver' in window)) { images.forEach(loadDeferred); return; }
    var watcher = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        watcher.unobserve(entry.target);
        if (entry.target.tagName === 'IMG') loadDeferred(entry.target);
        else Array.prototype.forEach.call(entry.target.querySelectorAll('img[data-defer]'), loadDeferred);
      });
    }, { rootMargin: '200px 0px' });
    var watched = [];
    images.forEach(function (img) {
      var group = img.closest('[data-defer-group]');
      var target = group || img;
      if (watched.indexOf(target) === -1) { watched.push(target); watcher.observe(target); }
    });
    window.addEventListener('beforeprint', function () {
      Array.prototype.forEach.call(document.querySelectorAll('img[data-defer]'), loadDeferred);
    });
  }

  /* Card hover pictures. The second picture has no src until a pointer or keyboard focus first reaches
     its card, and the card swaps to it only once it has loaded. Touch screens never fetch it. */
  function initCardHover() {
    if (!window.matchMedia('(hover: hover)').matches) return;
    function arm(event) {
      var card = event.target.closest ? event.target.closest('.product-card--has-hover') : null;
      if (!card || card.hasAttribute('data-hover-armed')) return;
      card.setAttribute('data-hover-armed', '');
      var img = card.querySelector('img[data-hover-src]');
      if (!img) return;
      img.addEventListener('load', function () { card.classList.add('is-hover-ready'); }, { once: true });
      var srcset = img.getAttribute('data-hover-srcset');
      if (srcset) img.srcset = srcset;
      img.src = img.getAttribute('data-hover-src');
    }
    document.addEventListener('pointerover', arm);
    document.addEventListener('focusin', arm);
  }

  initSiteTopHeight();
  initStickyHeader();
  initAnnouncements();
  initMobileMenu();
  initMobileGroups();
  initNavDropdowns();
  initCartCount();
  initFormStatus();
  initDeferredImages();
  initCardHover();
  document.addEventListener('shopify:section:load', function (event) { initDeferredImages(event.target); });
})();
